
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Box
} from '@mui/material';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';

import { API_BASE_URL } from '../lib/constants';
import { useNotification } from '../contexts/NotificationContext';

const EditInventory = () => {
  const { showNotification } = useNotification();
  const { item_id } = useParams();
  const [formData, setFormData] = useState({
    product_name: '',
    quantity: '',
    price: '',
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchItemData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/inventory/${item_id}`);
        setFormData(response.data);
      } catch (error) {
        console.error('Error fetching item data:', error);
        showNotification('error', 'Failed to fetch inventory item details.');
      }
    };
    fetchItemData();
  }, [item_id]);

  const validate = () => {
    const tempErrors = {};
    if (!formData.product_name || !formData.product_name.trim()) {
      tempErrors.product_name = 'Product name is required';
    } else if (formData.product_name.trim().length < 2) {
      tempErrors.product_name = 'Product name too short';
    }

    if (!formData.quantity || formData.quantity <= 0) tempErrors.quantity = 'Valid quantity is required';
    if (!formData.price || formData.price <= 0) tempErrors.price = 'Valid price is required';

    setErrors(tempErrors);
    const hasError = Object.keys(tempErrors).length > 0;
    if (hasError) {
      showNotification('error', `Please correct: ${Object.values(tempErrors)[0]}`);
    }
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity" && value.length > 3) return;
    if (name === "price" && value.length > 6) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    switch (name) {
      case 'product_name':
        if (!value.trim()) error = 'Product name is required';
        else if (value.trim().length < 2) error = 'Product name must be at least 2 characters long';
        break;
      case 'quantity':
        if (!value) error = 'Quantity is required';
        else if (value <= 0) error = 'Quantity must be positive';
        else if (value > 999) error = 'Quantity cannot exceed 999';
        break;
      case 'price':
        if (!value) error = 'Price is required';
        else if (value <= 0) error = 'Price must be positive';
        else if (value > 999999) error = 'Price cannot exceed 999,999';
        break;
      default: break;
    }
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      await axios.put(`${API_BASE_URL}/inventory/${item_id}`, formData);
      showNotification('success', `Item "${formData.product_name}" updated successfully!`);
      setTimeout(() => navigate('/home', { state: { activeMenu: 'Inventory' } }), 1000);
    } catch (error) {
      console.error('Error updating item:', error);
      const msg = error.response?.data?.error || 'Failed to update item. Check server connection.';
      showNotification('error', msg);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'Inventory' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            <Typography variant="h4" sx={{ color: '#008080', fontWeight: 600, letterSpacing: 0.5 }}>
              EDIT INVENTORY ITEM
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography variant="h6" sx={{ color: '#008080', marginLeft: 1, fontWeight: 500 }}>
                OTMS
              </Typography>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Product Name"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.product_name}
                  helperText={errors.product_name}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  inputProps={{ max: 999 }}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.quantity}
                  helperText={errors.quantity}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price}
                  inputProps={{ max: 999999 }}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.price}
                  helperText={errors.price}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary" fullWidth>
                  Update Item
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default EditInventory;

